import { ArrowLeftIcon } from '@phosphor-icons/react'
import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { TOOLBAR_CONTAINER } from 'app/components/CompactLabel'
import { cn } from 'app/lib/utils'

/**
 * The header every board shares. It sits at the page gutter like every other page's
 * PageHeader: these boards once centred their header in a narrower column than the lanes
 * beneath it, so a title sat inset from the cards it named. The title is drawn the way
 * PageHeader draws one, with the same space beneath it, so switching a list between its
 * board and table views moves nothing above the toolbar row.
 */
export function ListBoardHeader({
  backTo,
  backLabel = `Action Center`,
  title,
  titleMarker,
  subheader,
  totalItems,
  actions,
  headerControls,
  controls,
  viewToggle,
  banner,
}: {
  /** Where the back link goes. Leave undefined to draw no back link: a list page is its own home. */
  backTo?: string
  /** The back link's text. */
  backLabel?: string
  title: string
  /** Drawn right after the title, such as a personal list's lock. */
  titleMarker?: ReactNode
  /** A quiet line under the title row, such as a list's kind; PageHeader draws it the same way. */
  subheader?: ReactNode
  /** The list's count, beside the title, the chip PageHeader draws on the table view. */
  totalItems?: number
  /** The right end of the title row, such as a list's Settings and "Add contacts". */
  actions?: ReactNode
  /**
   * The very end of the controls row's right half, after `viewToggle`: the list page's view
   * switch. The table views end their own toolbar with the same element
   * (`trailingControls` on TableLayout, `rightControls` on TreatmentTable), so the switch sits
   * at the same right edge in every view and never moves under the cursor that just used it.
   */
  headerControls?: ReactNode
  /** What narrows the list. Leave undefined to draw no controls row at all. */
  controls?: ReactNode
  /** What changes how the list is drawn, such as a sort picker. The right half, before `headerControls`. */
  viewToggle?: ReactNode
  /** A reading or failure notice. The caller styles it, since the boards say it differently. */
  banner?: ReactNode
}) {
  const titleRow = (
    // Wraps rather than squeezes: a long title on a phone used to break the chip into two lines.
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
      <h3 className="gilroy text-strong">{title}</h3>
      {titleMarker}
      {typeof totalItems === `number` && (
        <div className="border-default flex shrink-0 items-center justify-center whitespace-nowrap rounded border-[0.5px] bg-gray-minimal px-1.5 py-0.5 text-label-xs text-gray-strong">
          <p>
            {totalItems.toLocaleString(`en-US`)} {totalItems === 1 ? `item` : `items`}
          </p>
        </div>
      )}
    </div>
  )
  const heading = subheader ? (
    <div>
      {titleRow}
      {subheader}
    </div>
  ) : (
    titleRow
  )

  return (
    <div className="w-full shrink-0">
      {backTo && (
        <Link
          to={backTo}
          className="inline-flex items-center gap-1.5 text-body-sm text-subtle transition hover:text-strong"
        >
          <ArrowLeftIcon size={14} weight="bold" /> {backLabel}
        </Link>
      )}

      <div className={cn(`flex flex-col`, backTo && `mt-3`)}>
        {/* The same space PageHeader leaves under a title, so the board's toolbar row starts
            where the table's does. */}
        <div className="flex items-center justify-between gap-3 pb-6 md:pb-8">
          {heading}
          {actions && <div className="flex items-center gap-3">{actions}</div>}
        </div>
        {/* One row, two halves: what narrows the list on the left, what changes how it is
            drawn and then the list page's own controls on the right. On a phone the left half
            wraps under Search, so the right end sits on its last line. */}
        {(controls !== undefined || headerControls) && (
          <div className="flex w-full flex-wrap items-center justify-between gap-4 max-md:items-end">
            {/* The container CompactLabel measures: labels shorten before the row wraps. */}
            {/* On a phone the first control (Search, on every board) takes a line of its own
                and the rest share the next one as icons: three and four wrapped rows used to
                push the lanes a third of the way down the screen. */}
            <div
              className={cn(
                `flex min-w-0 flex-1 flex-wrap items-center gap-2`,
                `max-md:[&>*:first-child]:basis-full max-md:[&>*:first-child>div]:w-full`,
                TOOLBAR_CONTAINER,
              )}
            >
              {controls}
            </div>
            {(viewToggle || headerControls) && (
              <div className="flex shrink-0 items-center gap-2">
                {viewToggle}
                {headerControls}
              </div>
            )}
          </div>
        )}
      </div>

      {banner}
    </div>
  )
}
